import React, { useState } from 'react';
import { Table, TableColumnsType, Button, Space, Modal, message, Tag } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import AbAddressFilterArea, {
  AbAddressFilterOptions,
  DEFAULT_AB_ADDRESS_FILTER_OPTIONS,
} from './components/AbAddressFilterArea';
import AbAddressCreateModal from './components/AbAddressCreateModal';
import AbAddressDetailDrawer from './components/AbAddressDetailDrawer';
import {
  GET_AB_ADDRESS_MAPPING_LISTING,
  DELETE_AB_ADDRESS_MAPPING,
  GetAbAddressMappingListingResponse,
  AbAddressMappingType,
} from './graphql/abAddressGraphql';
import { translateGraphQLError } from '../../../utils/graphqlUtils';

const PAGE_SIZE = 10;

const reviewStatusTag = (status: number, t: (k: string) => string) => {
  switch (status) {
    case 1:
      return <Tag color="green">{t('wms.abAddress.status.approved')}</Tag>;
    case 2:
      return <Tag color="red">{t('wms.abAddress.status.rejected')}</Tag>;
    case 0:
    default:
      return <Tag color="orange">{t('wms.abAddress.status.pending')}</Tag>;
  }
};

const AbAddressManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouseContext();

  const [filterOptions, setFilterOptions] = useState<AbAddressFilterOptions>(
    DEFAULT_AB_ADDRESS_FILTER_OPTIONS
  );
  const [appliedFilters, setAppliedFilters] = useState<AbAddressFilterOptions>(
    DEFAULT_AB_ADDRESS_FILTER_OPTIONS
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  // 当前站点 ID：HUB / WHS 都用自身 id
  const currentSiteId = currentWarehouse?.id;

  const { data, loading, refetch } = useQuery<GetAbAddressMappingListingResponse>(
    GET_AB_ADDRESS_MAPPING_LISTING,
    {
      variables: {
        hubId: currentSiteId ? Number(currentSiteId) : undefined,
        id: appliedFilters.id ? Number(appliedFilters.id) : undefined,
        aAddress: appliedFilters.aAddress || undefined,
        bAddress: appliedFilters.bAddress || undefined,
        reporterName: appliedFilters.reporterName || undefined,
        status: appliedFilters.status != null && appliedFilters.status !== ''
          ? Number(appliedFilters.status)
          : undefined,
        page: currentPage,
        size: PAGE_SIZE,
      },
      skip: !currentSiteId,
      fetchPolicy: 'cache-and-network',
    }
  );

  const [deleteMapping] = useMutation(DELETE_AB_ADDRESS_MAPPING, {
    onCompleted: () => {
      message.success(t('wms.abAddress.messages.deleteSuccess') as string);
      refetch();
    },
    onError: (err) => message.error(translateGraphQLError(err, 'errors.system.unknownError')),
  });

  const handleFilter = () => {
    setAppliedFilters({ ...filterOptions });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilterOptions(DEFAULT_AB_ADDRESS_FILTER_OPTIONS);
    setAppliedFilters(DEFAULT_AB_ADDRESS_FILTER_OPTIONS);
    setCurrentPage(1);
  };

  const handleTableChange = (page: number) => {
    setCurrentPage(page);
  };

  const showDeleteConfirm = (record: AbAddressMappingType) => {
    Modal.confirm({
      title: t('wms.abAddress.deleteTitle') as string,
      okText: t('components.button.confirm') as string,
      okType: 'danger',
      cancelText: t('components.button.cancel') as string,
      onOk: () => deleteMapping({ variables: { id: Number(record.id) } }),
    });
  };

  const columns: TableColumnsType<AbAddressMappingType> = [
    {
      title: t('wms.abAddress.table.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: t('wms.abAddress.table.aAddress'),
      dataIndex: 'aFullAddress',
      key: 'aFullAddress',
      ellipsis: true,
      render: (v: string | null | undefined) => v ?? '—',
    },
    {
      title: t('wms.abAddress.table.bAddress'),
      dataIndex: 'bFullAddress',
      key: 'bFullAddress',
      ellipsis: true,
      render: (v: string | null | undefined) => v ?? '—',
    },
    {
      title: t('wms.abAddress.table.reporter'),
      key: 'reporter',
      width: 140,
      align: 'center',
      render: (_, record) => record.reporterName ?? record.reporterAccountId ?? '—',
    },
    {
      title: t('wms.abAddress.table.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      align: 'center',
      render: (v: string | null | undefined) =>
        v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—',
    },
    {
      title: t('wms.abAddress.table.status'),
      dataIndex: 'reviewStatus',
      key: 'reviewStatus',
      width: 110,
      align: 'center',
      render: (v: number) => reviewStatusTag(v, t),
    },
    {
      title: t('components.button.action') as string,
      key: 'action',
      width: 140,
      fixed: 'right' as const,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" size="small" onClick={() => setDetailId(record.id)}>
            {t('components.button.view')}
          </Button>
          <Button type="link" size="small" danger onClick={() => showDeleteConfirm(record)}>
            {t('components.button.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  const pageData = data?.getAbAddressMappingListing;

  return (
    <div className="p-6">
      <AbAddressFilterArea filters={filterOptions} setFilters={setFilterOptions} />

      <div className="flex justify-between items-center mb-4 mt-4">
        <Button
          type="primary"
          onClick={() => setCreateOpen(true)}
          disabled={!currentSiteId}
          className="w-auto min-w-24"
        >
          <span className="font-bold">{t('wms.abAddress.createButton')}</span>
        </Button>
        <div className="flex items-center gap-2">
          <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.filter')}</span>
          </Button>
          <Button onClick={handleResetFilters} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.reset')}</span>
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <Table
          columns={columns}
          dataSource={pageData?.content || []}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: PAGE_SIZE,
            total: pageData?.totalElements || 0,
            showSizeChanger: false,
            showTotal: (total) => t('wms.abAddress.table.paginationTotal', { total }),
            onChange: handleTableChange,
          }}
        />
      </div>

      {currentSiteId && (
        <AbAddressCreateModal
          open={createOpen}
          hubId={currentSiteId}
          onClose={() => setCreateOpen(false)}
          onSuccess={() => {
            setCreateOpen(false);
            refetch();
          }}
        />
      )}

      <AbAddressDetailDrawer
        open={detailId != null}
        id={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
};

export default AbAddressManagementPage;
